import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Loader2, PawPrint } from 'lucide-react'

import { auth, ownerAuth } from '../api/client'
import { Card, CardContent } from '../components/ui/card'

/**
 * Phase 6B — landing route for both LINE-callback redirects AND magic-link
 * consume redirects. The backend 302s the browser to:
 *
 *   /auth/callback#token=<access>&refresh_token=<refresh>&is_new=<bool>
 *
 * Magic-link clicks come in as:
 *
 *   /auth/callback?owner_magic_token=<secret>
 *
 * In that case we re-hit the backend's /auth/owner/magic-link/consume
 * endpoint (so the browser-binding cookie travels), which itself 302s
 * to this same page with the hash form.
 */
export default function AuthCallback() {
  const { t } = useTranslation()
  const nav = useNavigate()
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  useEffect(() => {
    const url = new URL(window.location.href)

    // Branch 1: hash form, post-LINE-callback / post-magic-link-consume.
    if (window.location.hash) {
      const hp = new URLSearchParams(window.location.hash.slice(1))
      const access = hp.get('token')
      const refresh = hp.get('refresh_token')
      if (access && refresh) {
        auth.setPair(access, refresh)
        // Scrub the hash so a back-button doesn't re-trigger.
        window.history.replaceState(null, '', '/')
        nav('/', { replace: true })
        return
      }
    }

    // Branch 2: query form, the magic-link URL the user clicked from email.
    const magicToken = url.searchParams.get('owner_magic_token')
    if (magicToken) {
      // Hit the consume endpoint via a top-level navigation so the
      // browser sends the ownerlink_bind cookie + receives the 302 to
      // the hash form above.
      const base = (import.meta.env.VITE_API_BASE || '') + '/api/v1'
      window.location.replace(
        `${base}/auth/owner/magic-link/consume?owner_magic_token=${encodeURIComponent(magicToken)}`,
      )
      return
    }

    // Neither — error. The backend would have hit /login?error=… directly,
    // so landing here without either form is a bug or a stale link.
    setErrorMsg(t('auth.error.invalid'))
    const timer = setTimeout(() => nav('/login', { replace: true }), 2500)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Reference ownerAuth so tree-shaking doesn't drop the lineStartUrl
  // export we may need for re-entry. Cheap no-op.
  void ownerAuth

  return (
    <div className="container max-w-md py-16">
      <div className="mb-10 flex items-center gap-2 text-base font-semibold">
        <div className="grid size-9 place-items-center rounded-lg bg-primary text-primary-foreground">
          <PawPrint className="size-5" />
        </div>
        <span>PetPro</span>
      </div>
      <Card>
        <CardContent className="flex items-center gap-3 py-10">
          {errorMsg ? (
            <span className="text-sm text-destructive">{errorMsg}</span>
          ) : (
            <>
              <Loader2 className="size-4 animate-spin text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                {t('auth.callback.signingIn')}
              </span>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
